import { dialog } from "nq-component";
import BaseListPresenter from "../../../../base/BaseListPresenter";
import {
  CreditsDeduction,
  formatDate,
} from "../../../../components/common/Converter";
import { findObjectUseCase, upsertUseCase } from "../../../../usecases/object";

class CancelRequestPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }
  init() {
    this.limit = 10;
    this.where = {
      stats: "Cancel Request",
    };
    this.search = {};
    this.filter = {};
    this.include = [
      "employee",
      "employee.position",
      "employee.salaryPointer",
      "leaveType",
    ];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  onChangeFilter(type, value, field) {
    // Copy the current filters from the state
    const where = { ...this.view.state.filters };

    // Apply the new filter
    switch (type) {
      case "Pointer":
        if (Object.keys(value).length > 0) {
          where[field] = { id: value.id };
        } else {
          delete where[field]; // Remove filter if no value
        }
        break;
      case "Boolean":
        if (value !== null) {
          where[field] = value;
        } else {
          delete where[field]; // Remove filter if no value
        }
        break;
      default:
        if (value) {
          where[field] = { $regex: value, $options: "i" };
        } else {
          delete where[field]; // Remove filter if no value
        }
    }
    console.log("where: ", this.view.state.filters);
    this.view.setState({ filters: where }, () => {
      this.filterSubmit(this.view.state.filters);
    });
  }

  async handleConfirmationDialog(status, object) {
    this.view
      .showConfirmDialog("Click Okay to proceed.", status)
      .then(() => {
        if (status === "Approve") {
          dialog.close();
          this.handleApprove(object);
        } else {
          dialog.close();
          this.handleDissaprove(object);
        }
      })
      .catch(() => {});
  }

  async handleApprove(object) {
    const { employee, leaveType, ...data } = object;
    data.leaveType = { id: leaveType.id };
    data.employee = { id: employee.id };

    const allAvailableCredit = await findObjectUseCase().execute("leave_cred", {
      where: {
        empID: employee.id,
        // type: data.leaveType.name,
      },
    });

    const currentCredits = allAvailableCredit.filter(
      (item) => item.type === leaveType.name,
    );

    const creditDeduct = parseFloat(data.creditDeduction) || 0;

    const hasVacationLeave = allAvailableCredit.some(
      (item) => item.type === "Vacation Leave",
    );

    if (data.stats === "Cancelled") {
      dialog.close();
      this.view.showSuccess("Your Request already Cancelled");
      return;
    }

    if (
      leaveType.name === "Sick Leave" &&
      hasVacationLeave &&
      data.stats !== "Disapproved" &&
      data.stats !== "Cancelled" &&
      data.stats !== "Pending" &&
      data.categoryType !== "Without Pay"
    ) {
      this.handleSickLeaveWithVacationDeduct(
        data,
        creditDeduct,
        allAvailableCredit,
      );
      return;
    }
    if (
      leaveType.name === "Wellness Leave" &&
      hasVacationLeave &&
      data.stats !== "Disapproved" &&
      data.stats !== "Cancelled" &&
      data.stats !== "Pending" &&
      data.categoryType !== "Without Pay"
    ) {
      this.handleWellnessLeaveDeduct(data, creditDeduct, allAvailableCredit);
      return;
    }

    if (
      leaveType.name === "Forced/Mandatory Leave" &&
      hasVacationLeave &&
      data.stats !== "Disapproved" &&
      data.stats !== "Cancelled" &&
      data.stats !== "Pending" &&
      data.categoryType !== "Without Pay"
    ) {
      this.handleForcedLeave(data, creditDeduct, allAvailableCredit);
      return;
    }

    // return;

    if (
      currentCredits.length > 0 &&
      data.stats !== "Disapproved" &&
      data.stats !== "Cancelled"
    ) {
      const leaveCred = parseFloat(currentCredits[0].current) + creditDeduct;
      currentCredits[0].current = leaveCred.toFixed(3);

      if (
        (leaveType.name === "Sick Leave" && !hasVacationLeave) ||
        leaveType.name === "Forced/Mandatory Leave" ||
        leaveType.name === "Vacation Leave"
      ) {
        this.GenerateLeaveCard(currentCredits[0], data, creditDeduct);
      }
      await upsertUseCase().execute("leave_cred", currentCredits[0]);
    }
    data.stats = "Cancelled";
    await upsertUseCase().execute("leave_request", data);
    this.view.refetchData();
    dialog.close();
  }

  async handleSickLeaveWithVacationDeduct(
    data,
    creditDeduct,
    allAvailableCredit,
  ) {
    const vacationBackCred = data?.vacationLeaveDeduction
      ? parseFloat(data?.vacationLeaveDeduction)
      : 0;
    const sickBackCred = creditDeduct - vacationBackCred;
    console.log("SICK: ", sickBackCred);
    console.log("VACATION: ", vacationBackCred);

    const availableCredit = allAvailableCredit.filter(
      (item) => item.type === "Vacation Leave" || item.type === "Sick Leave",
    );

    const credit = CreditsDeduction(
      availableCredit,
      vacationBackCred,
      sickBackCred,
      0,
    );
    data.stats = "Cancelled";

    data.vacationLeaveDeduction = credit.vacationLeaveDeduction;

    // handle Leave Card
    const vacationLeave = credit.updatedCredits.find(
      (item) => item.type === "Vacation Leave",
    );
    const sickLeave = credit.updatedCredits.find(
      (item) => item.type === "Sick Leave",
    );

    credit.updatedCredits.forEach(async (item) => {
      await upsertUseCase().execute("leave_cred", item);
    });

    this.GenerateLeaveCard(vacationLeave, data, vacationBackCred);
    this.GenerateLeaveCard(sickLeave, data, sickBackCred);
    await upsertUseCase().execute("leave_request", data);
    this.view.refetchData();
    dialog.close();
    return;
  }

  async handleWellnessLeaveDeduct(data, creditDeduct, allAvailableCredit) {
    const vacationBackCred = data?.vacationLeaveDeduction
      ? parseFloat(data?.vacationLeaveDeduction)
      : 0;
    const wellnessBackCred = creditDeduct - vacationBackCred;

    const wellnessLeave = allAvailableCredit.find(
      (item) => item.type === "Wellness Leave",
    );

    if (vacationBackCred > 0) {
      const vacationLeave = allAvailableCredit.find(
        (item) => item.type === "Vacation Leave",
      );

      vacationLeave.current = (
        parseFloat(vacationLeave.current) + vacationBackCred
      ).toFixed(3);
      await upsertUseCase().execute("leave_cred", vacationLeave);
      this.GenerateLeaveCard(vacationLeave, data, vacationBackCred);
    }

    wellnessLeave.current = (
      parseFloat(wellnessLeave.current) + wellnessBackCred
    ).toFixed(3);
    await upsertUseCase().execute("leave_cred", wellnessLeave);
    this.GenerateLeaveCard(wellnessLeave, data, wellnessBackCred);

    data.stats = "Cancelled";
    await upsertUseCase().execute("leave_request", data);
    this.view.refetchData();
    dialog.close();
    return;
  }

  async handleForcedLeave(data, creditDeduct, allAvailableCredit) {
    data.stats = "Cancelled";

    const vacationLeave = allAvailableCredit.find(
      (item) => item.type === "Vacation Leave",
    );
    const forcedLeave = allAvailableCredit.find(
      (item) => item.type === "Forced/Mandatory Leave",
    );

    const creditsVacation = parseFloat(vacationLeave.current) + creditDeduct;
    let creditsForced = parseFloat(forcedLeave.current) + creditDeduct;
    if (creditDeduct > 5) {
      creditsForced = 5;
    }

    vacationLeave.current = creditsVacation.toFixed(3);
    forcedLeave.current = creditsForced.toFixed(3);

    try {
      await upsertUseCase().execute("leave_cred", vacationLeave);
      await upsertUseCase().execute("leave_cred", forcedLeave);
      await upsertUseCase().execute("leave_request", data);
      this.view.refetchData();
      dialog.close();
      return;
    } catch (e) {
      console.error("Error: ", e);
    }
  }

  async GenerateLeaveCard(findLeaveType, submitData, earned) {
    const generateLeaveCard = {
      employee: { id: findLeaveType.empID },
      leaveType: findLeaveType.type,
      earned: earned.toFixed(3),
      balance: findLeaveType.current,
      startDate: submitData.startdate,
      endDate: submitData.enddate,
      categoryType: submitData.categoryType,
      status: "Cancelled",
    };
    console.log("SUBIT: ", submitData);
    try {
      await upsertUseCase().execute("leave_card", generateLeaveCard);
    } catch (e) {
      console.error("E: ", e);
    }
  }

  // dissaproved

  async handleDissaprove(object) {
    const collection = this.view.getCollectionName();
    const data = {
      stats: object?.oldStatus,
      id: object.id,
    };
    try {
      await upsertUseCase().execute(collection, data);
      this.view.refetchData();
      this.view.showSuccess("Cancel Request has been disapproved.");
    } catch (error) {
      this.view.showError(error.message || error);
      console.error("Error in handleDissaprove: ", error.message || error);
    }
  }
}
export default CancelRequestPagePresenter;
